import React from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AlertCircle, Building2, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  ADDED_CHOICE,
  choiceValue,
  isDecided,
  toChooseLabel,
  twinHeldBy,
  withChoice,
  type MappingDecisions,
  type MappingErrorItem,
  type MappingListing,
  type MappingRoomRow,
  type RoomMappingPreview,
} from '@/lib/combined-inventory'

interface RoomMappingEditorProps {
  preview: RoomMappingPreview | null
  decisions: MappingDecisions | null
  onChange: (next: MappingDecisions) => void
  /** The server is redrawing the step for the latest decisions. */
  loading: boolean
  error: string | null
  /** A refused save, sentence by sentence; a sentence that names a room is shown on that room. */
  saveErrors?: MappingErrorItem[] | null
}

// THE ROOM MAPPING STEP: one glance, one click ("Optimistic room mapping" in
// docs/plans/2026-10-16-combined-inventory.md).
//
// One row per room of each following listing: its name, its room count, and ONE choice, "Same as
// <lead room> · N rooms" or "Added room (not at the lead)". Confident matches arrive already chosen, so
// the usual visit is open, look, Save. The only thing that holds Save is a row with no choice yet,
// marked "Choose one". Rate plans pair by name when the mapping is saved, and anything left unsettled
// is named after saving with a link to where it is fixed, so there is nothing else to decide here.
//
// The operator's choices live in the parent and change on a click; the option labels and the
// "N rooms in the building" line are the server's answer, redrawn a moment later.
//
// This is the JOIN step (creating a combined inventory, adding a listing). Once a listing has joined, its
// mapping, its Same rooms' offsets and their rate plans are changed on its room mapping page (§3.20).
export default function RoomMappingEditor({ preview, decisions, onChange, loading, error, saveErrors }: RoomMappingEditorProps) {
  if (error) {
    return (
      <div role="alert" className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-800">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    )
  }

  if (!preview || !decisions) return <MappingSkeleton />

  const generalErrors = (saveErrors ?? []).filter((item) => item.roomTypeId == null)
  const roomErrors = new Map<number, string[]>()
  for (const item of saveErrors ?? []) {
    if (item.roomTypeId == null) continue
    roomErrors.set(item.roomTypeId, [...(roomErrors.get(item.roomTypeId) ?? []), item.message])
  }

  const toChoose = preview.listings.flatMap((listing) => listing.rooms).filter((room) => !isDecided(decisions[room.id])).length

  return (
    <div className="space-y-3">
      {toChoose > 0 && (
        <p className="flex justify-end" aria-live="polite">
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">{toChooseLabel(toChoose)}</span>
        </p>
      )}

      {generalErrors.length > 0 && (
        <div role="alert" className="space-y-1 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-3 text-sm text-rose-800">
          {generalErrors.map((item) => (
            <p key={item.message} className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{item.message}</span>
            </p>
          ))}
        </div>
      )}

      {preview.listings.map((listing) => (
        <ListingCard
          key={listing.propertyId}
          listing={listing}
          preview={preview}
          decisions={decisions}
          onChange={onChange}
          roomErrors={roomErrors}
        />
      ))}

      <p className="flex min-h-5 items-center justify-end gap-1.5 px-1 text-xs tabular-nums text-muted-foreground" aria-live="polite">
        {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-label="Updating" />}
        {preview.countLine}
      </p>
    </div>
  )
}

// ── One following listing ────────────────────────────────────────────────────────────────────────
function ListingCard({
  listing,
  preview,
  decisions,
  onChange,
  roomErrors,
}: {
  listing: MappingListing
  preview: RoomMappingPreview
  decisions: MappingDecisions
  onChange: (next: MappingDecisions) => void
  roomErrors: Map<number, string[]>
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm" aria-label={`${listing.name} rooms`}>
      <div className="flex items-center gap-2 border-b border-border/40 px-4 py-2.5">
        <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="truncate text-sm font-semibold text-foreground">{listing.name}</span>
      </div>

      {listing.rooms.length === 0 ? (
        <p className="px-4 py-5 text-center text-xs text-muted-foreground">{listing.name} has no rooms yet, so there is nothing to map.</p>
      ) : (
        <ul className="divide-y divide-border/40">
          {listing.rooms.map((room) => (
            <RoomRow
              key={room.id}
              room={room}
              listing={listing}
              preview={preview}
              decisions={decisions}
              onChange={onChange}
              errors={roomErrors.get(room.id) ?? []}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

// ── One room: its name, its count, its one choice ────────────────────────────────────────────────
function RoomRow({
  room,
  listing,
  preview,
  decisions,
  onChange,
  errors,
}: {
  room: MappingRoomRow
  listing: MappingListing
  preview: RoomMappingPreview
  decisions: MappingDecisions
  onChange: (next: MappingDecisions) => void
  errors: string[]
}) {
  const decision = decisions[room.id]
  const open = !isDecided(decision)

  return (
    <li className="px-4 py-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{room.name}</p>
          <p className="text-xs tabular-nums text-muted-foreground">{room.roomsLabel}</p>
        </div>
        <Select
          // '' keeps the select controlled while the room has no choice; Radix shows the placeholder.
          value={choiceValue(decision)}
          onValueChange={(value) => onChange(withChoice(decisions, room.id, value))}
        >
          <SelectTrigger
            className={cn(
              'h-9 w-full shrink-0 text-xs focus-visible:ring-2 focus-visible:ring-primary/40 sm:w-72',
              open && 'border-amber-300 text-amber-700',
            )}
            aria-label={`What ${room.name} is`}
          >
            <SelectValue placeholder="Choose one" />
          </SelectTrigger>
          <SelectContent>
            {preview.leadRooms.map((leadRoom) => {
              const heldBy = twinHeldBy(listing, decisions, leadRoom.id, room.id)
              return (
                <SelectItem key={leadRoom.id} value={String(leadRoom.id)} disabled={heldBy != null} className="text-xs">
                  {leadRoom.label}
                  {heldBy ? ` · chosen for ${heldBy.name}` : ''}
                </SelectItem>
              )
            })}
            {preview.leadRooms.length > 0 && <SelectSeparator />}
            <SelectItem value={ADDED_CHOICE} className="text-xs">
              {preview.addedOptionLabel}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {errors.map((message) => (
        <p key={message} role="alert" className="mt-2 flex items-start gap-1.5 text-xs text-rose-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>{message}</span>
        </p>
      ))}
    </li>
  )
}

function MappingSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true" aria-label="Loading the room mapping">
      <Skeleton className="h-40 w-full rounded-xl" />
      <Skeleton className="ml-auto h-4 w-40" />
    </div>
  )
}
